import React, { useContext } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Alert, Button, Modal,
} from '@printdeal/ui-library';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import OrderDetailContext, { OrderDetailContextAfterLoad } from '../../../contexts/OrderDetailContext';
import { ServiceFactory } from '../../../../../../services/ServiceFactory';
import { dispatchAccountEvent } from '../../../../../../helpers/dataLayerHelpers/accountDataLayerHelper';

interface Props {
  orderId: string;
  lineItemIds?: string[];
  customLineItemIds?: string[];
  handleCloseModal: (eventName: string) => void;
}

const orderService = ServiceFactory.createOrderService();

const CancelOrderModal = ({
  orderId,
  lineItemIds = [],
  customLineItemIds = [],
  handleCloseModal,
}: Props) => {
  const intl = useIntl();
  const { order } = useContext(OrderDetailContext) as OrderDetailContextAfterLoad;
  const queryClient = useQueryClient();
  const cancelLineItemMutation = useMutation({
    mutationFn: orderService.cancelOrder,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sanitized-order', order?.id] });
      dispatchAccountEvent('order_detail_interaction', 'cancel_order');
    },
    onError: () => {
      dispatchAccountEvent('order_detail_api_failure', 'cancel_order');
    },
  });

  const handleCloseModalExtended = (eventName: string) => {
    cancelLineItemMutation.reset();
    handleCloseModal(eventName);
  };

  const cancelling = cancelLineItemMutation.isPending;

  return (
    <Modal
      isOpen
      onClose={() => handleCloseModalExtended('close_modal_cancel_order_generic')}
      dismissLabel={intl.formatMessage({ id: 'account.general.modal.close', defaultMessage: 'Close' })}
      title={intl.formatMessage({ id: 'orderDetails.cancelOrder.modal.title', defaultMessage: 'Cancel order' })}
      data-cy="cancelOrderModal"
    >
      <Modal.Content>
        {cancelLineItemMutation.status === 'error' && (
          <Alert variant="error" className="pd-mb-4">
            <FormattedMessage id="orderDetails.cancelOrder.modal.error" defaultMessage="error while cancelling order" />
          </Alert>
        )}
        <FormattedMessage
          id="orderDetails.cancelOrder.modal.text"
          defaultMessage="Are you certain you want to cancel this order?"
        />
      </Modal.Content>
      <Modal.Footer className="pd-flex pd-flex-row-reverse pd-flex-wrap pd-gap-2">
        <Button
          data-cy="confirmCancelOrderButton"
          variant="primary"
          disabled={cancelling}
          isLoading={cancelling}
          onClick={() => cancelLineItemMutation.mutate({ orderId, lineItemIds, customLineItemIds })}
        >
          <FormattedMessage
            id="orderDetails.cancelOrder.modal.confirmButton"
            defaultMessage="Cancel order"
          />
        </Button>
        <Button
          variant="secondary"
          onClick={() => handleCloseModalExtended('close_modal_cancel_order_by_button')}
          data-cy="cancelButtonCancelOrder"
        >
          <FormattedMessage id="order.cancelOrder.modal.close" defaultMessage="Close" />
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CancelOrderModal;
